import React, { useMemo, Children, Component } from 'react';
import { motion } from 'framer-motion';
import { Trophy, Star, CheckCircle, Circle, Sparkles } from 'lucide-react';
import { AppState } from '../../types';
import { GroupId } from '../../config/systemConfig';
import { MODULES } from '../../data/modules';
import { getModuleDisplayInfo } from '../../config/moduleContent';
import { useAppStore } from '../../hooks/useAppStore';
import { Card } from '../ui/Card';
import { PageHeader } from '../shared/PageHeader';
import * as Icons from 'lucide-react';
interface ProgressPageProps {
  state: AppState;
  selectedGroup: GroupId;
}
export function ProgressPageRedesigned({
  state,
  selectedGroup
}: ProgressPageProps) {
  const {
    getModuleMasteryForGroup,
    getAnsweredQuestions
  } = useAppStore();
  // Calculate overall progress
  const {
    masteredCount,
    inProgressCount,
    notStartedCount
  } = useMemo(() => {
    let mastered = 0;
    let inProgress = 0;
    let notStarted = 0;
    MODULES.forEach(m => {
      const mastery = getModuleMasteryForGroup(m.id);
      const answered = getAnsweredQuestions(m.id).length;
      if (mastery >= 95) mastered++;else if (answered > 0 || mastery > 0) inProgress++;else notStarted++;
    });
    return {
      masteredCount: mastered,
      inProgressCount: inProgress,
      notStartedCount: notStarted
    };
  }, [getModuleMasteryForGroup, getAnsweredQuestions]);
  // Encouraging message based on progress
  const getMessage = () => {
    if (masteredCount === MODULES.length) return {
      emoji: '🏆',
      text: 'Wauw! Je hebt alle vakken afgerond!'
    };
    if (masteredCount >= MODULES.length / 2) return {
      emoji: '🌟',
      text: 'Geweldig! Je bent al over de helft!'
    };
    if (inProgressCount > 0) return {
      emoji: '💪',
      text: 'Goed bezig! Blijf zo doorgaan!'
    };
    return {
      emoji: '👋',
      text: 'Begin met oefenen om je voortgang te zien!'
    };
  };
  const message = getMessage();
  const containerVariants = {
    hidden: {
      opacity: 0
    },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.08
      }
    }
  };
  const itemVariants = {
    hidden: {
      opacity: 0,
      y: 16
    },
    visible: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.35,
        ease: 'easeOut'
      }
    }
  };
  return <motion.div className="space-y-8 pb-12" variants={containerVariants} initial="hidden" animate="visible" data-id="element-4899">
      {/* Page Header */}
      <motion.div variants={itemVariants} data-id="element-4900">
        <PageHeader icon={Trophy} title="Mijn Voortgang" subtitle="Zo gaat het met jouw vakken" badge={`Groep ${selectedGroup}`} accentColor="bg-amber-100 text-amber-600" data-id="element-4901" />
      </motion.div>

      {/* Summary Card - Report Card Style */}
      <motion.div variants={itemVariants} data-id="element-4902">
        <Card className="p-6 sm:p-8 bg-gradient-to-br from-amber-50 to-orange-50 border-amber-100" data-id="element-4903">
          <div className="flex items-center gap-4 mb-6" data-id="element-4904">
            <div className="text-5xl" data-id="element-4905">{message.emoji}</div>
            <div data-id="element-4906">
              <h2 className="text-xl font-black text-slate-900" data-id="element-4907">
                {message.text}
              </h2>
              <p className="text-slate-600 font-medium" data-id="element-4908">
                {masteredCount} van {MODULES.length} vakken afgerond
              </p>
            </div>
          </div>

          {/* Visual progress - Stars */}
          <div className="flex items-center gap-3 flex-wrap" data-id="element-4909">
            {MODULES.map(m => {
            const mastery = getModuleMasteryForGroup(m.id);
            const isMastered = mastery >= 95;
            return <div key={m.id} className={`w-10 h-10 rounded-full flex items-center justify-center transition-all ${isMastered ? 'bg-amber-400 text-white shadow-md' : 'bg-white border-2 border-slate-200 text-slate-300'}`} title={m.title} data-id="element-4910">
                  <Star className={`w-5 h-5 ${isMastered ? 'fill-current' : ''}`} data-id="element-4911" />
                </div>;
          })}
          </div>
        </Card>
      </motion.div>

      {/* Subject Progress List - Report Card Style */}
      <motion.div variants={itemVariants} data-id="element-4912">
        <h2 className="text-xl font-bold text-slate-900 mb-4" data-id="element-4913">Jouw vakken</h2>
        <div className="space-y-3" data-id="element-4914">
          {MODULES.map((module, index) => <SubjectProgressRow key={module.id} module={module} selectedGroup={selectedGroup} index={index} getModuleMasteryForGroup={getModuleMasteryForGroup} getAnsweredQuestions={getAnsweredQuestions} data-id="element-4915" />)}
        </div>
      </motion.div>

      {/* Encouragement Footer */}
      <motion.div variants={itemVariants} data-id="element-4916">
        <Card className="p-6 bg-indigo-50 border-indigo-100 text-center" data-id="element-4917">
          <Sparkles className="w-8 h-8 text-indigo-500 mx-auto mb-3" data-id="element-4918" />
          <p className="text-slate-700 font-semibold" data-id="element-4919">
            Elke dag een beetje oefenen maakt je steeds beter!
          </p>
        </Card>
      </motion.div>
    </motion.div>;
}
// Subject Progress Row - Clean, report-card style
function SubjectProgressRow({
  module,
  selectedGroup,
  index,
  getModuleMasteryForGroup,
  getAnsweredQuestions
}: {
  module: (typeof MODULES)[0];
  selectedGroup: GroupId;
  index: number;
  getModuleMasteryForGroup: (moduleId: string) => number;
  getAnsweredQuestions: (moduleId: string) => any[];
}) {
  const IconComponent = (Icons as any)[module.icon] || Icons.BookOpen;
  const displayInfo = getModuleDisplayInfo(module.id, selectedGroup);
  const title = displayInfo?.title || module.title;
  const mastery = getModuleMasteryForGroup(module.id);
  const answered = getAnsweredQuestions(module.id).length;
  const isStarted = answered > 0 || mastery > 0;
  const isMastered = mastery >= 95;
  const colorMap: Record<string, {
    bg: string;
    icon: string;
  }> = {
    'text-blue-500': {
      bg: 'bg-blue-50',
      icon: 'text-blue-600'
    },
    'text-indigo-500': {
      bg: 'bg-indigo-50',
      icon: 'text-indigo-600'
    },
    'text-emerald-500': {
      bg: 'bg-emerald-50',
      icon: 'text-emerald-600'
    },
    'text-rose-500': {
      bg: 'bg-rose-50',
      icon: 'text-rose-600'
    },
    'text-amber-500': {
      bg: 'bg-amber-50',
      icon: 'text-amber-600'
    },
    'text-purple-500': {
      bg: 'bg-purple-50',
      icon: 'text-purple-600'
    },
    'text-teal-500': {
      bg: 'bg-teal-50',
      icon: 'text-teal-600'
    },
    'text-cyan-500': {
      bg: 'bg-cyan-50',
      icon: 'text-cyan-600'
    }
  };
  const colors = colorMap[module.color] || {
    bg: 'bg-slate-50',
    icon: 'text-slate-600'
  };
  // Status indicator
  const getStatus = () => {
    if (isMastered) return {
      label: 'Klaar!',
      color: 'text-emerald-600',
      bg: 'bg-emerald-100',
      icon: CheckCircle
    };
    if (mastery >= 50) return {
      label: 'Bijna klaar',
      color: 'text-amber-600',
      bg: 'bg-amber-100',
      icon: Sparkles
    };
    if (isStarted) return {
      label: 'Bezig',
      color: 'text-indigo-600',
      bg: 'bg-indigo-100',
      icon: Circle
    };
    return {
      label: 'Nieuw',
      color: 'text-slate-400',
      bg: 'bg-slate-100',
      icon: Circle
    };
  };
  const status = getStatus();
  const StatusIcon = status.icon;
  return <motion.div initial={{
    opacity: 0,
    x: -20
  }} animate={{
    opacity: 1,
    x: 0
  }} transition={{
    delay: index * 0.05
  }} data-id="element-4920">
      <Card className={`p-4 flex items-center gap-4 ${isMastered ? 'border-emerald-200 bg-emerald-50/50' : ''}`} data-id="element-4921">
        {/* Icon */}
        <div className={`p-3 rounded-xl ${colors.bg} flex-shrink-0`} data-id="element-4922">
          <IconComponent className={`w-6 h-6 ${colors.icon}`} strokeWidth={2.5} data-id="element-4923" />
        </div>

        {/* Title */}
        <div className="flex-1 min-w-0" data-id="element-4924">
          <h3 className="font-bold text-slate-900 truncate" data-id="element-4925">{title}</h3>
          {isStarted && !isMastered && <div className="flex gap-1 mt-1.5" data-id="element-4926">
              {[0, 1, 2, 3, 4].map(i => <div key={i} className={`h-1.5 flex-1 rounded-full ${i < Math.ceil(mastery / 20) ? mastery >= 50 ? 'bg-amber-400' : 'bg-indigo-400' : 'bg-slate-200'}`} data-id="element-4927" />)}
            </div>}
        </div>

        {/* Status Badge */}
        <div className={`flex items-center gap-1.5 px-3 py-1.5 rounded-full ${status.bg}`} data-id="element-4928">
          <StatusIcon className={`w-4 h-4 ${status.color} ${isMastered ? 'fill-current' : ''}`} data-id="element-4929" />
          <span className={`text-xs font-bold ${status.color}`} data-id="element-4930">
            {status.label}
          </span>
        </div>
      </Card>
    </motion.div>;
}